import { useLazyQuery } from '@apollo/client';
import { Button } from '@superops/ui-design-system';
import React, { useState, useContext, useEffect } from 'react';
import {
  DropdownArrow,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/mangifera/src/Pebbles/select';
import {
  COMMON_CONSTANTS,
  getTranslatedText,
  useActivityTracker,
  useCheckFeatureGating,
  useProduct,
  useSiteListForDropdown
} from '@nile/components';
import { AccountClient, GET_CLIENTS } from '@nile/graphql';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import Select from 'react-select';
import { cloneDeep, isArray, isEmpty } from 'lodash';
import DateFilter from '@nile/reports/src/modules/reports/utils/DateFilter';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  DEFAULT_VIEW,
  DEFAULT_CONDITION_ENUM,
  DATA_REPORT,
  PDF_REPORT,
  EFFICIENCY_REPORT,
  PROFITABLITY_REPORT,
  EFFICIENCY_REPORTS,
  INVOICE_STATEMENTS
} from '@nile/reports/src/modules/home/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';
import { getYearDate } from '../../../../EfficiencyReport/util';

/**
 * Component to display report filter
 * @param props
 * @returns JSX
 */
const ReportFilter = (props) => {
  const {
    intl,
    reportType,
    showSite = false,
    showDateFilter = true,
    showClient = true
  } = props;

  const flags = useFlags();
  const [sites, setSites] = useState([]);
  const reportsContext = useContext(ReportsContext);

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSiteOption, setSelectedSiteOption] = useState(null);
  const [options, setOptions] = useState([]);
  const [loadingClient, setLoadingClient] = useState(true);

  const isMSPProduct = useProduct('MSP');
  const isNewContractEnabled = useCheckFeatureGating('CONTRACT_V3')?.enabled;
  const isEfficiencyReport = isNewContractEnabled
    ? EFFICIENCY_REPORTS.includes(reportsContext?.activeReport?.internalName)
    : reportType === EFFICIENCY_REPORT;
  const isInvoiceStatements =
    reportsContext?.activeReport?.internalName === INVOICE_STATEMENTS;

  const reportCondFromLD = !isEmpty(flags?.defaultReportOrder)
    ? Object.values(flags?.defaultReportOrder)
    : [];

  const activeReportFilter =
    reportsContext?.reportCondition &&
    reportsContext?.reportCondition[reportsContext.activeView];

  const dateFromContext =
    activeReportFilter?.find((el) => el.attribute === 'createdTime') || {};

  const clientFromContext =
    activeReportFilter?.find((el) => el.attribute === 'client') || {};

  const [date, setDate] = useState(
    (dateFromContext && dateFromContext.value) || null
  );

  const setFilters = () => {
    if (reportType === DATA_REPORT) {
      const isClientReport = reportsContext.activeReport.entity === 'Client';
      const isRequesterModule =
        reportsContext.activeReport.entity === 'RequesterAssociation';
      if (isClientReport || isRequesterModule) {
        setDate(null);
        if (isClientReport) setSelectedOption(null);
        return;
      }
    }
    if (reportsContext?.activeView === DEFAULT_VIEW) {
      const filter = reportCondFromLD.find(
        (el) => el?.internalName === reportsContext?.activeReport?.internalName
      );
      const filterEnum =
        filter && DEFAULT_CONDITION_ENUM[filter.defaultCondition];
      setDate(getYearDate(filterEnum) || null);
      if (clientFromContext) {
        const selectedClient = options.find(
          (el) => el.accountId === clientFromContext?.value
        );
        setSelectedOption(selectedClient || null);
      } else {
        setSelectedOption(null);
      }
      return;
    }

    setDate((dateFromContext && dateFromContext.value) || null);
    if (clientFromContext) {
      const selectedClient = options.find(
        (el) => el.accountId === clientFromContext?.value
      );
      setSelectedOption(selectedClient || null);
    } else {
      setSelectedOption(null);
    }
  };
  const { getClientSites, data: siteList } = useSiteListForDropdown();

  useEffect(() => {
    if (siteList) {
      const processedSites = siteList.map((site) => {
        return {
          label: site.name,
          value: site.name,
          ...site
        };
      });
      setSites(processedSites || []);
    }
  }, [siteList]);

  const handleClientChange = (selectedClient) => {
    setSelectedOption(selectedClient);
    setSelectedSiteOption(null);
    if (reportType === PDF_REPORT) {
      getClientSites(selectedClient.accountId);
    }
  };

  const conditions = {
    joinOperator: 'AND',
    operands: [
      { attribute: 'stage', operatorValue: 'placeholder.client.status.active' }
    ]
  };

  const [getClients, { loading, data }] = useLazyQuery(GET_CLIENTS, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: {
      listInfo: conditions
        ? {
            // condition: conditions,
            // Removing condition as we've introduced custom stage
            sort: [
              {
                attribute: 'name',
                order: 'ASC'
              }
            ]
          }
        : {
            sort: [
              {
                attribute: 'name',
                order: 'ASC'
              }
            ]
          }
    }
  });

  const checkForReset = () => {
    if (
      reportsContext.activeView === DEFAULT_VIEW &&
      !isEmpty(activeReportFilter)
    ) {
      const filter = reportCondFromLD?.find(
        (el) => el?.internalName === reportsContext?.activeReport?.internalName
      );
      const defaultDate = getYearDate(
        DEFAULT_CONDITION_ENUM[filter?.defaultCondition]
      );
      const equalValue = isArray(dateFromContext.value)
        ? defaultDate?.toString() === dateFromContext?.value?.toString()
        : defaultDate === dateFromContext.value;
      if (filter && dateFromContext && equalValue) {
        if (activeReportFilter?.length > 1) return true;
        return false;
      }
    }
    if (isEmpty(activeReportFilter)) return false;
    return true;
  };
  const showReset = checkForReset();

  const resetFilterCondition = () => {
    if (
      reportsContext.activeView === DEFAULT_VIEW &&
      reportType !== PDF_REPORT
    ) {
      const filter = reportCondFromLD.find(
        (el) => el.internalName === reportsContext.activeReport.internalName
      );
      setDate(
        (filter && DEFAULT_CONDITION_ENUM[filter.defaultCondition]) || null
      );
      setSelectedOption(null);
      reportsContext.setReportCondition({
        ...reportsContext.reportCondition,
        [reportsContext.activeView]:
          (filter &&
            DEFAULT_CONDITION_ENUM[filter.defaultCondition] && [
              {
                attribute: 'createdTime',
                operator: 'on',
                value: DEFAULT_CONDITION_ENUM[filter.defaultCondition]
              }
            ]) ||
          null
      });
      return;
    }
    setDate(null);
    setSites([]);
    setSelectedOption(null);
    setSelectedSiteOption(null);
    reportsContext.setReportCondition({
      ...reportsContext.reportCondition,
      [reportsContext.activeView]: null
    });
  };

  const disableFilter = () => {
    if (reportType === DATA_REPORT) {
      return (
        reportsContext.activeReport.entity === 'RequesterAssociation' ||
        reportsContext.activeReport.entity === 'Client' ||
        reportsContext.activeReport.entity === 'AllAssets'
      );
    }
    if (reportCondFromLD) {
      const reportConditionFromLD = reportCondFromLD?.find(
        (el) => el?.internalName === reportsContext?.activeReport?.internalName
      );

      if (reportConditionFromLD?.defaultCondition === 'DISABLE') {
        return true;
      }
    }
    return false;
  };

  const disableClientSelect = () => {
    if (reportType === DATA_REPORT) {
      return reportsContext.activeReport.entity === 'Client';
    }
    return false;
  };

  const disableClient = disableClientSelect();
  const dateDisabled = disableFilter();
  /**
   * Effect to handle report filter condition
   */
  useEffect(() => {
    if (date) {
      const dateObj = {
        attribute: 'createdTime',
        operator: typeof date === 'string' ? 'on' : 'between',
        value: date
      };
      if (isEfficiencyReport) {
        useActivityTracker({
          event: 'EFFICIENCY_TIME_SERIES_CHANGE',
          data: {
            date: date
          }
        });
      }
      if (activeReportFilter) {
        const updatedCondition = cloneDeep(activeReportFilter);
        const existingIndex = updatedCondition.findIndex(
          (cond) => cond.attribute === 'createdTime'
        );
        if (existingIndex >= 0) {
          updatedCondition[existingIndex] = dateObj;
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: updatedCondition
          });
        } else {
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: [...updatedCondition, dateObj]
          });
        }
      } else {
        reportsContext.setReportCondition({
          ...reportsContext.reportCondition,
          [reportsContext.activeView]: [dateObj]
        });
      }
    } else {
      reportsContext.setReportCondition({
        ...reportsContext.reportCondition,
        [reportsContext.activeView]:
          (activeReportFilter &&
            activeReportFilter.filter(
              (el) => el.attribute !== 'createdTime'
            )) ||
          null
      });
    }
  }, [date]);

  useEffect(() => {
    if (data?.getClientList) {
      const clients = JSON.parse(JSON.stringify(data?.getClientList?.clients));
      for (let i = 0; i < clients.length; i++) {
        clients[i].label = clients[i].name;
        clients[i].value = clients[i].name;
      }
      setOptions(clients);
      if (clientFromContext) {
        const selectedClient = clients.find(
          (el) => el.accountId === clientFromContext?.value
        );
        setSelectedOption(selectedClient);
      }
      setLoadingClient(false);
    }
  }, [data]);

  useEffect(() => {
    if (selectedOption) {
      const clientObj = {
        attribute: 'client',
        operator: 'is',
        value: selectedOption.accountId,
        label: selectedOption.label
      };
      if (activeReportFilter) {
        const updatedCondition = cloneDeep(activeReportFilter);
        const existingIndex = updatedCondition.findIndex(
          (cond) => cond.attribute === 'client'
        );
        if (existingIndex >= 0) {
          updatedCondition[existingIndex] = clientObj;
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: updatedCondition
          });
        } else {
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: [...updatedCondition, clientObj]
          });
        }
      } else {
        reportsContext.setReportCondition({
          ...reportsContext.reportCondition,
          [reportsContext.activeView]: [clientObj]
        });
      }
    } else {
      reportsContext.setReportCondition({
        ...reportsContext.reportCondition,
        [reportsContext.activeView]:
          (activeReportFilter &&
            activeReportFilter.filter((el) => el.attribute !== 'client')) ||
          null
      });
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedSiteOption) {
      const siteObj = {
        attribute: 'site',
        operator: 'is',
        value: selectedSiteOption.id,
        label: selectedSiteOption.label
      };
      if (activeReportFilter) {
        const updatedCondition = cloneDeep(activeReportFilter);
        const existingIndex = updatedCondition.findIndex(
          (cond) => cond.attribute === 'site'
        );
        if (existingIndex >= 0) {
          updatedCondition[existingIndex] = siteObj;
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: updatedCondition
          });
        } else {
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: [...updatedCondition, siteObj]
          });
        }
      } else {
        reportsContext.setReportCondition({
          ...reportsContext.reportCondition,
          [reportsContext.activeView]: [siteObj]
        });
      }
    } else {
      reportsContext.setReportCondition({
        ...reportsContext.reportCondition,
        [reportsContext.activeView]:
          (activeReportFilter &&
            activeReportFilter.filter((el) => el.attribute !== 'site')) ||
          null
      });
    }
  }, [selectedSiteOption]);

  useEffect(() => {
    resetFilterCondition();
  }, [reportsContext?.activeView]);

  useEffect(() => {
    setFilters();
  }, [reportsContext?.activeReport?.reportId, clientFromContext?.value]);

  useEffect(() => {
    if (isMSPProduct) {
      getClients();
    }
  }, []);

  return (
    <>
      {showReset && (
        <div className="mr8">
          <Button plain onClick={resetFilterCondition}>
            <FormattedMessage id="reports.report.filter.reset" />
          </Button>
        </div>
      )}
      <div className="bshadow card-border rounded-4px">
        <div className="flex">
          {showClient && isMSPProduct && (
            <div className="flex actr w250" disabled={disableClient}>
              <div className="flex actr ml-10">
                <SVGIcon name="ClientLogo" color="#666666" />
              </div>
              <Select
                className="ml-10 ellipsis fw"
                classNamePrefix="value_selector"
                formatGroupLabel={formatGroupLabel}
                value={selectedOption}
                components={{ IndicatorSeparator, DropdownArrow }}
                onChange={(val) => {
                  handleClientChange(val);
                }}
                isLoading={loadingClient}
                isDisabled={disableClient}
                options={options}
                placeholder={<FormattedMessage id="common.client" />}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                closeMenuOnScroll={(e) => {
                  return (
                    e.target &&
                    e.target.classList &&
                    !(e.target.classList.value.indexOf('menu-list') > -1)
                  );
                }}
                name="valueselector"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    borderBottom: 'none !important'
                  })
                }}
                noOptionsMessage={() => {
                  return loading
                    ? getTranslatedText(
                        intl,
                        'reports.report.filter.client.loading'
                      )
                    : getTranslatedText(
                        intl,
                        'reports.report.filter.client.none'
                      );
                }}
              />
            </div>
          )}
          {showSite && (
            <div className="w250">
              <div className="flex-row flex-align-center">
                <div className="flex actr ml-10">
                  <SVGIcon name="Site" color="#666666" />
                </div>
                <Select
                  className="ml-10 fw ellipsis"
                  classNamePrefix="value_selector"
                  formatGroupLabel={formatGroupLabel}
                  value={selectedSiteOption}
                  components={{ IndicatorSeparator, DropdownArrow }}
                  onChange={setSelectedSiteOption}
                  isLoading={loadingClient}
                  options={sites}
                  placeholder={
                    <FormattedMessage id="admin.policyManagement.hierarchy.selectSite.placeholder" />
                  }
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  closeMenuOnScroll={(e) => {
                    return (
                      e.target &&
                      e.target.classList &&
                      !(e.target.classList.value.indexOf('menu-list') > -1)
                    );
                  }}
                  name="valueselector"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      borderRadius: 0,
                      borderBottom: 'none !important'
                    })
                  }}
                  noOptionsMessage={() => {
                    return loading
                      ? getTranslatedText(intl, 'app.search.noOptions.pl')
                      : getTranslatedText(intl, 'app.search.noOptions.pl');
                  }}
                />
              </div>
            </div>
          )}
          {showDateFilter && (
            <div className="w250">
              <DateFilter
                setDate={setDate}
                date={isInvoiceStatements ? dateFromContext?.value : date}
                isNewLayout
                disabled={dateDisabled}
                isShowMonthPickerOnly={isEfficiencyReport}
                enableDatePicker={reportType !== PROFITABLITY_REPORT}
                isYearRangeOnly={reportType === PROFITABLITY_REPORT}
                placeholderLabel="common.label.date"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default injectIntl(ReportFilter);
